import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Upload, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface ImageUploadProps {
  onImageUploaded: (file: File, preview: string) => void;
}

export const ImageUpload = ({ onImageUploaded }: ImageUploadProps) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      validateAndSetFile(selectedFile);
    }
  };

  const validateAndSetFile = (selectedFile: File) => {
    // Check file type
    if (!selectedFile.type.includes("image/")) {
      toast({
        variant: "destructive",
        title: "Neplatný soubor",
        description: "Prosím nahrajte obrázek"
      });
      return;
    }

    // Check file size (max 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "Soubor je příliš velký",
        description: "Velikost souboru musí být menší než 10MB"
      });
      return;
    }

    const filePreview = URL.createObjectURL(selectedFile);
    setFile(selectedFile);
    setPreview(filePreview);
    onImageUploaded(selectedFile, filePreview);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const clearImage = () => {
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    setFile(null);
    setPreview(null);
    onImageUploaded(null as any, "");
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        {!preview ? (
          <div
            className={`border-2 border-dashed rounded-lg p-12 text-center ${
              isDragging ? "border-[#14A48D] bg-blue-50" : "border-gray-300"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center">
              <Upload className="h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">Nahrajte fotografii místnosti</h3>
              <p className="text-sm text-gray-500 mb-4">
                Přetáhněte soubor nebo klikněte pro výběr
              </p>
              <Label
                htmlFor="file-upload"
                className="cursor-pointer inline-flex items-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground px-4 py-2 text-sm font-medium"
              >
                Vybrat soubor
              </Label>
              <Input
                id="file-upload"
                type="file"
                className="sr-only"
                accept="image/*"
                onChange={handleFileChange}
              />
              <p className="mt-4 text-xs text-gray-400">
                Podporované formáty: JPEG, PNG. Maximální velikost: 10MB
              </p>
            </div>
          </div>
        ) : (
          <div className="relative">
            <img
              src={preview}
              alt="Náhled místnosti"
              className="w-full h-auto rounded-lg object-cover"
            />
            <Button
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={clearImage}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
